import type { SecurityHistoryFamily, SecurityHistoryPage } from "@closer/shared";
import {
  SECURITY_HISTORY_FAMILIES,
  SECURITY_HISTORY_FAMILY_NAMES,
  SECURITY_HISTORY_OUTCOME_NAMES,
} from "@closer/shared";
import { Badge, Button, Card, EmptyState, Spinner } from "@closer/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../lib/apiClient";

/**
 * Kişisel güvenlik geçmişi bölümü (CLOS-284 `[082]`).
 *
 * ### ⚠ Bu bir AUDIT tablosu DEĞİL
 *
 * Kart: *"audit ekranındaki kurumsal veya teknik alanlar AYNEN KOPYALANMAZ."* Ekranda
 * korelasyon kimliği, ham olay adı ya da kayıt kimliği yok — sunucu zaten göndermiyor.
 * Kullanıcının sorusu *"hesabıma ne oldu ve bu ben miydim?"*; cevabı dört alan veriyor:
 * ne oldu, ne zaman, hangi cihazdan, sonuç ne.
 *
 * ### ⚠ Sunucu verisi `useState`'e KOPYALANMIYOR
 *
 * `useInfiniteQuery` kullanılıyor (`apps/web/CLAUDE.md`: *"sunucu verisini `useState`'e
 * kopyalama"*). İlk yazımda elle `useState` + `fetch` yazmıştım: sayfa birikimini,
 * yükleniyor/hata durumlarını ve süzgeç değişiminde yarışan istekleri kendim yönetmem
 * gerekiyordu — üçü de kütüphanenin zaten çözdüğü ve elle yazıldığında bir gün bozulan
 * şeyler.
 *
 * ### ⚠ Sayfa NUMARASI yok, imleç var
 *
 * Geçmiş sürekli büyüyor. Numaralı sayfalamada kullanıcı ikinci sayfaya geçerken yeni bir
 * olay doğarsa bir satırı hiç görmezdi.
 */
export function SecurityHistorySection() {
  const [family, setFamily] = useState<SecurityHistoryFamily | "">("");

  const sorgu = useInfiniteQuery({
    queryKey: ["security-history", family],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: "20" });
      if (family) params.set("family", family);
      if (pageParam) params.set("cursor", pageParam);
      const { data } = await api<SecurityHistoryPage>(`/api/session/security-history?${params}`);
      return data;
    },
    getNextPageParam: (sonSayfa) => sonSayfa.nextCursor,
  });

  const items = sorgu.data?.pages.flatMap((s) => s.items) ?? [];

  return (
    <Card title="Güvenlik Geçmişi">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-fg-muted">
          Hesabınızda gerçekleşen giriş, parola ve güvenlik olayları. Bu geçmiş yalnız size görünür
          ve silinemez.
        </p>

        <div className="flex flex-wrap gap-2">
          <FiltreDugmesi secili={family === ""} onClick={() => setFamily("")}>
            Tümü
          </FiltreDugmesi>
          {SECURITY_HISTORY_FAMILIES.map((f) => (
            <FiltreDugmesi key={f} secili={family === f} onClick={() => setFamily(f)}>
              {SECURITY_HISTORY_FAMILY_NAMES[f]}
            </FiltreDugmesi>
          ))}
        </div>

        {sorgu.isPending ? <Spinner /> : null}

        {sorgu.isError ? (
          <p role="alert" className="text-sm text-danger">
            Güvenlik geçmişi yüklenemedi. Lütfen tekrar deneyin.
          </p>
        ) : null}

        {sorgu.isSuccess && items.length === 0 ? (
          <EmptyState
            title="Henüz kayıt yok"
            description="Bu filtreye uyan bir güvenlik olayı bulunmuyor."
          />
        ) : null}

        {items.length > 0 ? (
          <ul data-testid="guvenlik-gecmisi" className="flex flex-col gap-2">
            {items.map((olay) => (
              <li
                key={olay.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border-default p-3"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-fg">{olay.summary}</span>
                  <span className="text-xs text-fg-muted">
                    {new Date(olay.occurredAt).toLocaleString("tr-TR")}
                    {olay.device ? ` · ${olay.device}` : ""}
                  </span>
                </div>
                <Badge tone={olay.outcome === "failure" ? "danger" : "neutral"}>
                  {SECURITY_HISTORY_OUTCOME_NAMES[olay.outcome]}
                </Badge>
              </li>
            ))}
          </ul>
        ) : null}

        {sorgu.hasNextPage ? (
          <Button
            variant="secondary"
            onClick={() => void sorgu.fetchNextPage()}
            loading={sorgu.isFetchingNextPage}
          >
            Daha fazla göster
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

function FiltreDugmesi({
  secili,
  onClick,
  children,
}: {
  secili: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant={secili ? "primary" : "secondary"}
      size="sm"
      onClick={onClick}
      aria-pressed={secili}
      className="!h-7 !rounded-full !px-3 text-xs"
    >
      {children}
    </Button>
  );
}
